// src/components/plaid/plaidCopy.js
// Pooled inbox SP6 — every web string of Profile's Linked accounts panel, its
// Unlink dialog, the Settings "Turn off Plaid?" warning and the "Linked
// accounts" links (spec § Screens per app → Web; prototype
// proto/client/profile.html:93-106). The Questions page row's strings live in
// intake/intakeCopy.js. Screens and tests import keys; only
// tests/components/plaid-copy.test.js repeats the literals. Apostrophes are
// curly (R206). Everything the prototype does not draw is (proposed) and
// carries `// Wording pending` for Astra's copy pass.

// ---- Helpers ----------------------------------------------------------------------
// "Chase", "Chase and Fidelity", "Chase, Fidelity and Ally".
export const bankList = (names) =>
  names.length < 2
    ? names.join("")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
// The names that appear on more than one link (pack T5: two links at one
// bank are told apart by their account masks).
export const sharedNames = (links) => {
  const seen = new Set();
  const twice = new Set();
  links.forEach(({ institution: { name } }) =>
    (seen.has(name) ? twice : seen).add(name),
  );
  return twice;
};
// "Checking · Credit card", or "Checking ••1234 · Credit card ••9876" when the
// bank's name is shared.
export const accountsLine = (accounts, withMasks) =>
  accounts
    .map((a) => (withMasks && a.mask ? `${a.label} ••${a.mask}` : a.label))
    .join(" · ");

// ---- Profile › Linked accounts (proto profile.html:93-106) --------------------------
export const LINKED_TITLE = "Linked accounts";
export const LINKED_LEDE =
  "Banks and brokerages you connected through Plaid. They fill in your Debts and Investments answers; nothing here can move money.";
export const SEE_FIGURES = "See the figures";
export const LINK_ANOTHER = "Link another account";
// An ISO instant → "Linked Sep 18".
export const LINKED_ON = (iso) =>
  `Linked ${new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
export const LINKED_FOOT =
  "Read-only through Plaid. Unlinking stops Plaid reading that bank; your answers stay as you saved them."; // Wording pending
export const LINKED_NONE = "No accounts linked yet."; // Wording pending
export const LINK_FIRST = "Link an account with Plaid"; // Wording pending
export const NEEDS_ATTENTION = "Needs attention"; // Wording pending
export const RECONNECT = "Reconnect"; // Wording pending
export const UNLINK = "Unlink"; // Wording pending
export const UNLINK_NAMED = (name) => `Unlink ${name}`; // Wording pending
export const RECONNECT_NAMED = (name) => `Reconnect ${name}`; // Wording pending
export const UNLINKING = "Unlinking…"; // Wording pending
export const UNLINKED = (name) => `${name} unlinked.`; // Wording pending
export const LINKS_LOAD_FAILED =
  "We couldn’t load your linked accounts. Try again."; // Wording pending
export const UNLINK_FAILED = (name) => `We couldn’t unlink ${name}. Try again.`; // Wording pending
export const LINK_FAILED = "We couldn’t connect your accounts. Try again."; // Wording pending

// ---- The Unlink dialog ------------------------------------------------------------
export const UNLINK_TITLE = (name) => `Unlink ${name}?`; // Wording pending
// `detail` names the accounts when two links share the bank's name.
export const UNLINK_BODY = (name, detail) =>
  `Plaid will stop reading your ${name} accounts${detail ? ` (${detail})` : ""}, and we’ll delete the account activity we saved from them. Your Debts and Investments answers stay as you saved them. You can link ${name} again any time.`; // Wording pending
export const UNLINK_CANCEL = "Cancel"; // Wording pending
export const UNLINK_CONFIRM = "Unlink"; // Wording pending

// ---- Settings › Consents: switching Plaid off (owner item 1, amended) ---------------
export const TURN_OFF_TITLE = "Turn off Plaid?"; // Wording pending
export const TURN_OFF_BODY = (names) =>
  `This unlinks ${bankList(names)} so Plaid stops reading them, and deletes the account activity we saved from them. Your answers stay as you saved them.`; // Wording pending
// When the list could not be read, the warning still says what will happen.
export const TURN_OFF_BODY_ANY =
  "This unlinks every bank you linked through Plaid so Plaid stops reading them, and deletes the account activity we saved from them. Your answers stay as you saved them."; // Wording pending
export const TURN_OFF_CANCEL = "Cancel"; // Wording pending
export const TURN_OFF_CONFIRM = "Turn off and unlink"; // Wording pending
// SP3 spec :556, curly per R206.
export const DISCONNECT_FAILED =
  "We couldn’t disconnect your accounts. Try again.";
